/**
 * gamepad.js — a physical controller as an input frame.
 *
 * This exists because of what local two-player actually is on a desktop. The
 * keyboard scheme is built for ONE person with a mouse — their hand covers WASD
 * and the keys around it, and that is the whole left half of the board. A
 * second player sharing it gets the arrow keys and whatever punctuation is
 * within reach, which is a compromise for both of them: it constrains player
 * one's layout to leave room, and hands player two a scheme nobody would choose.
 *
 * A controller ends that trade. Plug one in and the second player has a proper
 * set of buttons, and player one's keyboard stops being a shared resource.
 *
 * THE MAPPING IS THE ONE THE TOUCH DECK WAS DERIVED FROM. The pads were laid
 * out by asking how these controls would sit on a modern controller and working
 * backwards; this is the forward direction of that same answer, which is why
 * the diamond is the face buttons and guard is the left trigger.
 *
 * Nothing here subscribes to events. The Gamepad API only reports state when
 * polled, so this is called once per simulation step from the game loop, the
 * same place every other input is read.
 */

import { noInput } from './sync';

/**
 * Standard-layout button indices, from the W3C Gamepad "standard" mapping.
 * Named rather than inlined because `buttons[6]` at a call site is a fact about
 * a spec and not about this game.
 */
const BTN = {
  A: 0, B: 1, X: 2, Y: 3,
  LB: 4, RB: 5,
  LT: 6, RT: 7,
  UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15,
};

/** How far the stick must leave centre to count. Matches the touch deadzone. */
const DEADZONE = 0.35;
/** A trigger is analogue; this is where it counts as pressed. */
const TRIGGER = 0.4;

/** @returns {boolean} whether a button index is currently down. */
const down = (pad, i) => {
  const b = pad.buttons[i];
  if (!b) return false;
  return typeof b === 'object' ? b.pressed || b.value > TRIGGER : b > TRIGGER;
};

/**
 * Read one controller as an input frame.
 *
 * Press-vs-hold is the caller's problem everywhere else in this game — the
 * keyboard has a held map and an edge-triggered one — so it is solved here too,
 * by diffing against the previous poll. Without it a held A button would kick
 * the board sixty times a second.
 *
 * @param {number} index      Which connected pad to read.
 * @param {object} previous   The last frame's raw button states, mutated here.
 * @returns {object|null} an input frame, or null if no pad is on that slot.
 */
export function readGamepad(index, previous) {
  const pads = typeof navigator !== 'undefined' && navigator.getGamepads
    ? navigator.getGamepads()
    : [];
  const pad = pads && pads[index];
  if (!pad || !pad.connected) return null;

  const input = noInput();
  const x = pad.axes[0] || 0;
  const y = pad.axes[1] || 0;

  // Stick or d-pad, whichever the player reaches for.
  input.left = x < -DEADZONE || down(pad, BTN.LEFT);
  input.right = x > DEADZONE || down(pad, BTN.RIGHT);
  input.jump = y < -DEADZONE || down(pad, BTN.UP);
  input.down = y > DEADZONE || down(pad, BTN.DOWN);

  // Held: the beam charges for as long as it is held, and guard is a stance.
  input.beam = down(pad, BTN.RT) || down(pad, BTN.X);
  input.guard = down(pad, BTN.LT);

  // Pressed: one event per push, however long the button is held.
  const edge = (i) => {
    const now = down(pad, i);
    const was = !!previous[i];
    previous[i] = now;
    return now && !was;
  };
  const attack = edge(BTN.RT) || edge(BTN.X);
  input.action = edge(BTN.A) || edge(BTN.RB);
  input.switchMode = edge(BTN.B);
  input.cluster = edge(BTN.Y);
  input.emp = edge(BTN.LB);
  // Melee reuses the attack and switch buttons, exactly as the keyboard does:
  // the two sets are never live at the same time, because one needs a board and
  // the other needs the board gone.
  input.punch = attack;
  input.kick = input.switchMode;

  return input;
}

/**
 * Which pad drives a given slot, or -1 for none.
 *
 * Two people on one machine (local multiplayer): with one controller the
 * assumption is the scenario this module was written for — somebody is on the
 * keyboard and mouse, and the person who just plugged in a pad is the second
 * player. A second controller takes player one, so two pads means neither of
 * them needs the keyboard at all.
 *
 * Otherwise there is one person here, and the first pad is theirs. Against
 * the AI, player 2 IS the AI, so the two-player rule would hand a lone
 * controller to a fighter it cannot drive — plugged in, it did nothing.
 *
 * @param {'player1'|'player2'} slot
 * @param {number} count How many controllers are connected.
 * @param {boolean} [twoPlayers] Local multiplayer: both slots are people.
 * @returns {number} pad index, or -1.
 */
export function padForSlot(slot, count, twoPlayers = true) {
  if (count <= 0) return -1;
  if (!twoPlayers) return slot === 'player1' ? 0 : -1;
  if (slot === 'player2') return 0;
  return count > 1 ? 1 : -1;
}

/** @returns {number} how many controllers are connected right now. */
export function padCount() {
  if (typeof navigator === 'undefined' || !navigator.getGamepads) return 0;
  const pads = navigator.getGamepads();
  if (!pads) return 0;
  let n = 0;
  for (let i = 0; i < pads.length; i += 1) {
    if (pads[i] && pads[i].connected) n += 1;
  }
  return n;
}
